/**
 * Máscara de terreno no formato `DDTM` v1 do pipeline de assets (docs/assets/README.md): bits do mais para o menos significativo,
 * stride = largura / 8 + 1, 1 = sólido. O servidor é a autoridade; o cliente usa a máscara só para desenhar a cratera.
 */
export const MAX_MASK_DIMENSION = 8192;
const HEADER_SIZE = 20;

export class TerrainMask {
  readonly stride: number;

  constructor(
    readonly width: number,
    readonly height: number,
    private readonly data: Uint8Array = new Uint8Array((Math.floor(width / 8) + 1) * height),
  ) {
    this.stride = Math.floor(width / 8) + 1;
    if (data.length !== this.stride * height) throw new RangeError(`Máscara ${width}x${height} precisa de ${this.stride * height} bytes.`);
  }

  isSolid(x: number, y: number): boolean {
    if (x < 0 || x >= this.width || y < 0 || y >= this.height) return false;
    return ((this.data[y * this.stride + (x >> 3)] ?? 0) & (0x80 >> (x & 7))) !== 0;
  }

  setSolid(x: number, y: number, solid: boolean): void {
    if (x < 0 || x >= this.width || y < 0 || y >= this.height) return;
    const index = y * this.stride + (x >> 3);
    const bit = 0x80 >> (x & 7);
    this.data[index] = solid ? (this.data[index] ?? 0) | bit : (this.data[index] ?? 0) & ~bit;
  }

  /** Remove do terreno tudo que a cratera cobre, centrada em (cx, cy). Devolve quantos pixels removeu. */
  dig(crater: TerrainMask, cx: number, cy: number): number {
    const left = cx - Math.trunc(crater.width / 2);
    const top = cy - Math.trunc(crater.height / 2);
    let removed = 0;
    for (let py = Math.max(0, -top); py < crater.height && top + py < this.height; py++) {
      for (let px = Math.max(0, -left); px < crater.width && left + px < this.width; px++) {
        if (crater.isSolid(px, py) && this.isSolid(left + px, top + py)) {
          this.setSolid(left + px, top + py, false);
          removed++;
        }
      }
    }
    return removed;
  }

  /** RGBA de 4 bytes por pixel: sólido na cor dada (alfa 255), vazio transparente. Serve de `ImageData` para desenhar ou apagar. */
  toRgba(r = 255, g = 255, b = 255): Uint8ClampedArray<ArrayBuffer> {
    const out = new Uint8ClampedArray(new ArrayBuffer(this.width * this.height * 4));
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (this.isSolid(x, y)) {
          const i = (y * this.width + x) * 4;
          out[i] = r;
          out[i + 1] = g;
          out[i + 2] = b;
          out[i + 3] = 255;
        }
      }
    }
    return out;
  }

  /** Copia para a máscara o retângulo `data` (RGBA, `width`×`height`, a partir de (x0, y0)): pixel com alfa >= 128 é sólido. */
  setFromAlpha(data: Uint8ClampedArray, x0: number, y0: number, width: number, height: number): void {
    for (let py = 0; py < height; py++) {
      for (let px = 0; px < width; px++) this.setSolid(x0 + px, y0 + py, (data[(py * width + px) * 4 + 3] ?? 0) >= 128);
    }
  }

  clone(): TerrainMask {
    return new TerrainMask(this.width, this.height, this.data.slice());
  }

  solidCount(): number {
    let count = 0;
    for (let y = 0; y < this.height; y++) for (let x = 0; x < this.width; x++) if (this.isSolid(x, y)) count++;
    return count;
  }
}

export function parseMask(file: Uint8Array): TerrainMask {
  if (file.length < HEADER_SIZE || String.fromCharCode(file[0]!, file[1]!, file[2]!, file[3]!) !== 'DDTM') {
    throw new Error('Arquivo de máscara inválido: assinatura DDTM ausente.');
  }
  if (file[4] !== 1 || file[5] !== 0) throw new Error(`Máscara em versão (${file[4]}) ou ordem de bits (${file[5]}) não suportada.`);
  const view = new DataView(file.buffer, file.byteOffset, file.byteLength);
  const width = view.getUint32(8, true);
  const height = view.getUint32(12, true);
  const stride = view.getUint32(16, true);
  if (width === 0 || height === 0 || width > MAX_MASK_DIMENSION || height > MAX_MASK_DIMENSION) throw new Error(`Dimensões fora do limite: ${width}x${height}.`);
  if (stride !== Math.floor(width / 8) + 1) throw new Error(`Stride ${stride} inesperado para largura ${width}.`);
  if (file.length - HEADER_SIZE !== stride * height) throw new Error(`Corpo da máscara com ${file.length - HEADER_SIZE} bytes; esperado ${stride * height}.`);
  return new TerrainMask(width, height, file.slice(HEADER_SIZE));
}
